import {
  type AudienceSpendData,
  type AudienceSpendSummaryData,
  BillingPlan,
  type BrandCategory,
  type BrandWarning,
  type GetBillingResponse,
  type GetLookalikeAudiencesStatsResponse
} from '@innovationdepartment/proxima-sdk-axios';
import type { DatePickerProps } from '@innovationdepartment/proxima-ui';
import type { DataIngestionViewProps } from './brandCustomer';
import type { MasterTokenAdAccount } from './integrations';
import type { Brand } from './stores/brand-store';

type BrandOverviewSection =
  | 'user'
  | 'brand'
  | 'billing'
  | 'audiencesSpend'
  | 'audiencesSpendSummary'
  | 'audiencesStats'
  | 'brandCategory'
  | 'brandWarnings'
  | 'campaigns'
  | 'masterAdAccounts';

export const BillingPlanLabels = {
  [BillingPlan.Trial]: 'Trial',
  [BillingPlan.Annual]: 'Yearly',
  [BillingPlan.MonthByMonth]: 'Monthly'
} as const;

type BrandOverviewLoadingState = { [key in BrandOverviewSection]?: boolean };

export type CommonLoadingProps = {
  apiLoading: BrandOverviewLoadingState;
  loading: boolean;
};

export type BrandWarningsProps = {
  brandWarnings: BrandWarning[];
};

export type BrandWarningsPagination = {
  pageSize: number;
  pageNumber: number;
  total: number;
};

export type BrandInfoProps = CommonLoadingProps & {
  brand: Brand;
  billing: GetBillingResponse | null;
  last30DaysOfAllBrandSpend: number | undefined;
  audienceSpendSummary: AudienceUsageProps['audienceSpendSummary'];
};

export type BrandProfileProps = CommonLoadingProps & {
  category?: BrandCategory;
  onEditBrandCategory: () => void;
  onEditDataIngestionPermissions: () => void;
};

export type AudienceUsageProps = CommonLoadingProps & {
  audienceSpend: AudienceSpendData;
  audienceSpendSummary: AudienceSpendSummaryData;
  audienceStats: GetLookalikeAudiencesStatsResponse;
  dateRange: DatePickerProps['dateRange'];
  last30DaysOfAllBrandSpend: number | undefined;
  onAudienceDateRangeSelect: (dateRange: DatePickerProps['dateRange']) => void;
};

export type BrandMasterTokenAdAccountProps = CommonLoadingProps & {
  masterAdAccounts: MasterTokenAdAccount[];
  onConnectAdAccount: (adAccount: MasterTokenAdAccount) => Promise<void>;
  onDisconnectAdAccount: (adAccount: MasterTokenAdAccount) => Promise<void>;
};

/* components types */
export type AudiencesSectionProps = AudienceUsageProps;

export type BrandSectionProps = BrandProfileProps & BrandInfoProps;

export type BrandSectionExtraProps = {
  onRefreshBrandBilling: () => Promise<void>;
};

export type BrandOverviewProps = BrandProfileProps &
  BrandInfoProps &
  AudiencesSectionProps &
  BrandWarningsProps &
  DataIngestionViewProps &
  BrandMasterTokenAdAccountProps &
  BrandSectionExtraProps;

/* useBrandOverview hook types */
export type BrandOverviewHookProps = Omit<
  BrandOverviewProps,
  'onEditBrandCategory' | 'onEditDataIngestionPermissions'
> & {
  onUpdateBrandCategory: (category: BrandCategory) => Promise<void>;
  onRefreshBrandBilling: () => Promise<void>;
  onUpsertBrandCustomerSettings: (updatedIsFileUploadAllowed: boolean) => Promise<void>;
};
